import { useEffect, useState } from "react";
import { X, Play } from "lucide-react";
import Video360Viewer from "@/components/Video360Viewer";
import ViewerErrorBoundary from "@/components/ViewerErrorBoundary";
import AiBadge from "@/components/AiBadge";
import { useMediaAiFlags } from "@/lib/aiFlags";
import { dispatchViewerClosed } from "@/components/PostViewerCta";
import { resolveMediaUrl, type MediaRef } from "@/lib/media";

/**
 * Hilfsfunktion: aus `MediaRef` die `media_id` extrahieren, falls vorhanden.
 * Poster-ID hat Vorrang; wenn kein expliziter Poster gesetzt ist (auto-Frame
 * aus dem Video), fällt sie auf die Video-media_id zurück, damit der
 * KI-Status des Videos das Poster mit-markiert.
 */
function extractPosterAiId(
    posterRef?: MediaRef,
    videoRef?: MediaRef,
): string | undefined {
    if (posterRef && typeof posterRef === "object" && posterRef.media_id) {
        return posterRef.media_id;
    }
    if (videoRef && typeof videoRef === "object" && videoRef.media_id) {
        return videoRef.media_id;
    }
    return undefined;
}

/**
 * Multi-Video-Gallery für die „Sehen Sie sich selbst um."-Section.
 *
 * - 1 Item  → Video360Viewer inline gerendert (klassische Solo-Ansicht).
 * - 2–4 Items → Grid mit Poster-Kacheln, Klick öffnet Modal mit Video360Viewer.
 * - 0 Items → Section-Body bleibt leer (Header rendert der Aufrufer).
 *
 * Grid-Layout:
 *   2 Items → 2 Spalten
 *   3 Items → 3 Spalten
 *   4 Items → 2×2 (auf lg-Breakpoint 4 Spalten für breite Screens)
 */
export type ImmersiveItem = {
    id?: string;
    title?: string;
    enabled?: boolean;
    video?: MediaRef;
    poster?: MediaRef;
};

type Props = {
    items: ImmersiveItem[];
    /** Solo-Mode-Prefix (1 Item) — Grid/Modal nutzen immer immersive-tile-* /
     *  immersive-modal-*.  Default: "home-video360" für Startseite. */
    soloTestIdPrefix?: string;
};

/**
 * Kachel-Poster-Loader. Priorität:
 *  1. Admin-CMS-Poster (`posterRef`) — falls gesetzt.
 *  2. Auto-generiertes Backend-Poster des Videos (`poster_url` aus `/info`)
 *     — greift bei 360°-Videos rectilinear entprojiziert.
 *  3. Kein Poster → Fallback-Gradient + Play-Icon (im Parent).
 * Ein Modul-Cache verhindert doppelte /info-Requests pro media_id.
 */
const posterInfoCache = new Map<string, string | null>();

function useAutoPoster(videoRef: MediaRef | undefined, posterRef: MediaRef | undefined): string {
    const explicit = resolveMediaUrl(posterRef, "");
    const [auto, setAuto] = useState<string>("");
    const videoId =
        !explicit && videoRef && typeof videoRef === "object" ? videoRef.media_id : undefined;

    useEffect(() => {
        if (explicit) {
            setAuto("");
            return;
        }
        if (!videoId) return;
        if (posterInfoCache.has(videoId)) {
            setAuto(posterInfoCache.get(videoId) || "");
            return;
        }
        let cancelled = false;
        const base = process.env.REACT_APP_BACKEND_URL || "";
        fetch(`${base}/api/media/${videoId}/info`, { credentials: "omit" })
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => {
                const p = (d && d.poster_url) || "";
                posterInfoCache.set(videoId, p || null);
                if (!cancelled) setAuto(p);
            })
            .catch(() => {
                posterInfoCache.set(videoId, null);
            });
        return () => {
            cancelled = true;
        };
    }, [videoId, explicit]);

    return explicit || auto || "";
}

/**
 * Kleiner Wrapper um Video360Viewer, der `useAutoPoster` nutzt um die
 * direkte R2-Poster-URL (schnell, CDN-cached) für das Layer-1-DOM-Overlay
 * zu ziehen. Wird sowohl für Solo-Mode als auch das Modal verwendet.
 */
function ImmersiveVideo360({
    videoRef,
    posterRef,
    testIdPrefix,
    autoPlay,
    autoTour,
    fallbackVideoUrl,
    fallbackPosterUrl,
    mediaTitle,
}: {
    videoRef?: MediaRef;
    posterRef?: MediaRef;
    testIdPrefix: string;
    autoPlay?: boolean;
    autoTour?: boolean;
    fallbackVideoUrl?: string;
    fallbackPosterUrl?: string;
    mediaTitle?: string;
}) {
    const posterFromInfo = useAutoPoster(videoRef, posterRef);
    const src = resolveMediaUrl(videoRef, fallbackVideoUrl || "");
    const poster = posterFromInfo || fallbackPosterUrl || "";
    const mediaId =
        videoRef && typeof videoRef === "object" ? videoRef.media_id : undefined;
    return (
        <Video360Viewer
            src={src}
            poster={poster}
            testIdPrefix={testIdPrefix}
            autoPlay={autoPlay}
            autoTour={autoTour}
            mediaId={mediaId}
            mediaTitle={mediaTitle}
        />
    );
}

/** Kachel-Component für Grid-Modus. Nutzt useAutoPoster für Fallback. */
function ImmersiveTile({
    item,
    index,
    onOpen,
}: {
    item: ImmersiveItem;
    index: number;
    onOpen: () => void;
}) {
    const posterUrl = useAutoPoster(item.video, item.poster);
    const aiId = extractPosterAiId(item.poster, item.video);
    const aiFlags = useMediaAiFlags([aiId]);
    const showAi = aiId ? aiFlags[aiId] === true : false;
    return (
        <button
            type="button"
            onClick={onOpen}
            className="group relative aspect-[16/9] overflow-hidden rounded-sm transition-transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-hf-gold"
            style={{
                background: posterUrl
                    ? "hsl(var(--hf-surface))"
                    : "linear-gradient(135deg, hsl(var(--hf-ink)) 0%, hsl(var(--hf-surface)) 100%)",
                border: "1px solid hsl(var(--hf-line))",
            }}
            data-testid={`immersive-tile-${item.id || index}`}
            aria-label={`${item.title || "360°-Video"} öffnen`}
        >
            {posterUrl && (
                <img
                    src={posterUrl}
                    alt={item.title || "360°-Video"}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                    data-testid={`immersive-tile-${item.id || index}-poster`}
                />
            )}
            {/* KI-Badge — dezent oben rechts, konsistent zu allen anderen
             *  Badges auf der Seite. Wird nur eingeblendet wenn das
             *  zugehörige Poster-Medium (oder das Video als Fallback) im
             *  Admin als `is_ai_generated=true` markiert ist. */}
            <AiBadge
                show={showAi}
                testId={`immersive-tile-${item.id || index}-ai-badge`}
            />
            {/* Overlay Verlauf für Textlesbarkeit */}
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "linear-gradient(180deg, hsl(var(--hf-ink) / 0) 40%, hsl(var(--hf-ink) / 0.75) 100%)",
                }}
                aria-hidden
            />
            {/* Play-Icon zentriert */}
            <div className="absolute inset-0 flex items-center justify-center">
                <span
                    className="inline-flex items-center justify-center w-16 h-16 rounded-full transition-transform group-hover:scale-110"
                    style={{
                        background: "hsl(var(--hf-gold) / 0.95)",
                        color: "hsl(var(--hf-ink))",
                    }}
                >
                    <Play size={22} fill="currentColor" strokeWidth={0} />
                </span>
            </div>
            {/* Titel unten */}
            <div className="absolute left-4 right-4 bottom-4 flex items-center gap-2">
                <span
                    className="text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-sm"
                    style={{
                        background: "hsl(var(--hf-gold))",
                        color: "hsl(var(--hf-ink))",
                    }}
                >
                    360° VIDEO
                </span>
                <span
                    className="font-display text-base md:text-lg leading-tight"
                    style={{ color: "hsl(var(--hf-bg))" }}
                >
                    {item.title || `Video ${index + 1}`}
                </span>
            </div>
        </button>
    );
}

export default function ImmersiveGallery({ items, soloTestIdPrefix = "home-video360" }: Props) {
    const [openIdx, setOpenIdx] = useState<number | null>(null);

    /** Schließt das Modal und emittiert den Post-Viewer-CTA-Trigger
     *  („video360"), damit die globale CTA-Card im Anschluss erscheinen
     *  kann. Session-/CMS-Guards prüft `PostViewerCta` selbst. */
    const closeModal = () => {
        setOpenIdx((prevIdx) => {
            if (prevIdx !== null) dispatchViewerClosed("video360");
            return null;
        });
    };

    useEffect(() => {
        if (openIdx === null) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") closeModal();
        };
        window.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [openIdx]);

    if (items.length === 0) return null;

    // Solo-Modus: nur ein Item → inline Video360Viewer wie zuvor.
    if (items.length === 1) {
        const it = items[0];
        return (
            <ViewerErrorBoundary
                testId={`${soloTestIdPrefix}-fallback`}
                fallbackTitle="360°-Video kann nicht gestartet werden"
            >
                <ImmersiveVideo360
                    videoRef={it.video}
                    posterRef={it.poster}
                    testIdPrefix={soloTestIdPrefix}
                    autoTour={true}
                    fallbackVideoUrl="/media/demo-360-web.mp4"
                    fallbackPosterUrl="/media/demo-360-poster.jpg"
                    mediaTitle={it.title}
                />
            </ViewerErrorBoundary>
        );
    }

    // Grid-Modus: 2-4 Items → Poster-Kacheln, Klick öffnet Modal.
    const gridCols =
        items.length === 2
            ? "grid-cols-1 sm:grid-cols-2"
            : items.length === 3
                ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4";

    const active = openIdx !== null ? items[openIdx] : null;

    return (
        <>
            <div className={`grid ${gridCols} gap-4 md:gap-6`} data-testid="immersive-gallery">
                {items.map((it, i) => {
                    return (
                        <ImmersiveTile
                            key={it.id || i}
                            item={it}
                            index={i}
                            onOpen={() => setOpenIdx(i)}
                        />
                    );
                })}
            </div>

            {active && (
                <div
                    className="fixed inset-0 z-[80] flex items-center justify-center p-4"
                    style={{ background: "hsl(var(--hf-ink) / 0.9)" }}
                    onClick={closeModal}
                    role="dialog"
                    aria-modal="true"
                    aria-label={active.title || "360°-Video"}
                    data-testid="immersive-modal"
                >
                    <div
                        className="relative w-full max-w-6xl rounded-sm overflow-hidden"
                        style={{ background: "hsl(var(--hf-bg))" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div
                            className="flex items-center justify-between px-5 py-3"
                            style={{ borderBottom: "1px solid hsl(var(--hf-line))" }}
                        >
                            <div>
                                <div
                                    className="hf-eyebrow"
                                    style={{ color: "hsl(var(--hf-gold-dark))" }}
                                >
                                    360°-Video
                                </div>
                                <div className="font-display text-lg">
                                    {active.title || "360°-Video"}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={closeModal}
                                className="p-2 rounded-sm hover:bg-hf-surface"
                                aria-label="Schließen"
                                data-testid="immersive-modal-close"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div style={{ background: "hsl(var(--hf-surface))" }}>
                            <ViewerErrorBoundary
                                testId="immersive-modal-fallback"
                                fallbackTitle="360°-Video kann nicht gestartet werden"
                            >
                                <ImmersiveVideo360
                                    // key erzwingt Re-Mount bei Kachelwechsel
                                    key={active.id || openIdx}
                                    videoRef={active.video}
                                    posterRef={active.poster}
                                    testIdPrefix={`immersive-modal-video360`}
                                    autoPlay={true}
                                    autoTour={true}
                                    mediaTitle={active.title}
                                />
                            </ViewerErrorBoundary>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
